import { useState } from "react";
import type { Editor } from "src/types/Editor";
import { worldToFixture, type CostFixture } from "./CostFixture";

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const formatTimestamp = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
  );
};

const download = (fixture: CostFixture) => {
  const blob = new Blob([JSON.stringify(fixture, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fixture.id}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export interface FixtureExport {
  busy: boolean;
  hasBuilding: boolean;
  worldHash: string;
  onClick: () => Promise<void>;
}

export function useFixtureExport(editor: Editor): FixtureExport {
  const [busy, setBusy] = useState(false);
  const world = editor.world;
  const hasBuilding = !!world?.hasBuilding;
  const worldHash = world?.rgWorldHashKey?.slice(0, 8) ?? "no-world";

  const onClick = async () => {
    if (busy || !world) return;
    if (!hasBuilding) {
      alert("No building to export — generate a design first.");
      return;
    }
    setBusy(true);
    try {
      const area = prompt("Area (city-country, e.g. 'London-UK'):", "London-UK") ?? "";
      if (!area) return;
      const slug = slugify(area);
      if (!slug) return;
      const id = `${slug}-${formatTimestamp(new Date())}`;
      const label = world.label || area;
      const fixture = await worldToFixture(world, { id, label, area });
      console.log(`[cost-fixture] exported ${id}`, fixture);
      try {
        download(fixture);
      } catch (err) {
        console.error("[cost-fixture] download failed, fixture JSON above", err);
        alert(
          "Download was blocked (check Chrome's 'Allow multiple downloads?' prompt near the URL bar). " +
            "Fixture JSON has been logged to the console — copy it from there."
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return { busy, hasBuilding, worldHash, onClick };
}
